import React, { useState } from "react";
import { ShieldCheck, BookOpen, Award, Phone, Video, Smartphone } from "lucide-react";
import { sendOtp, verifyOtp } from "../../api/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { storeAuthData } from "../../api/storageService";
import { useNavigate } from "react-router-dom"; 

const Login = () => {
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate(); 
    const handleSendOtp = async () => {
        if (!phone || phone.length !== 10) {
            toast.error("Enter valid 10 digit phone number");
            return;
        }

        try {
            setLoading(true);

            const res = await sendOtp(phone, "weblogin");

            if (res.status) {
                setOtpSent(true);
                toast.success("OTP sent successfully ✅"); // 👈 toaster
            } else {
                toast.error(res.message || "Failed to send OTP");
            }

        } catch (err) {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp) {
            toast.error("Enter OTP");
            return;
        }

        try {
            setLoading(true);

            const res = await verifyOtp(phone, otp, "weblogin");

            if (res.status) {
                   storeAuthData(res); 
                toast.success("Login successful 🎉"); // 👈 toaster

                console.log("Login success:", res.data);

                navigate("/dashboard");

            } else {
                toast.error("Invalid OTP");
            }

        } catch (err) {
            toast.error("OTP verification failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <><ToastContainer position="top-right" autoClose={3000} />
            <div className="custom-science custom-padding pt-80 pb-80 xl-screen-padding">
                <div className="w-full max-w-6xl grid md:grid-cols-2 gap-12">

                    <div className="hidden md:block">

                        <div className="bg-gray-100 text-gray-800 font-lato text-xs font-bold px-4 py-2 rounded-full inline-flex items-center gap-2 mb-6">
                            <ShieldCheck size={14} />
                            Secure student access for Nutrition Academy
                        </div>
                        <h1 className="text-4xl font-bold text-gray-900 leading-tight mb-4 font-lato font-bold ">
                            Continue your learning journey with a focused, trusted login experience.
                        </h1>

                        <p className="text-gray-500 mb-8 font-lato">
                            Sign in to access purchased courses, live classes, quiz history,
                            certificates, saved resources, and your personalized dashboard.
                        </p>
                        <div className="grid grid-cols-2 gap-4 mb-8 font-lato">
                            {[
                                { icon: <BookOpen size={18} />, title: "Your learning dashboard", desc: "Track progress and revisit lessons." },
                                { icon: <Award size={18} />, title: "Certificates and achievements", desc: "Access verified certificates." },
                                { icon: <Video size={18} />, title: "Live class access", desc: "Join sessions anytime." },
                                { icon: <Smartphone size={18} />, title: "Synced across devices", desc: "Continue learning anywhere." },
                            ].map((item, i) => (
                                <div key={i} className="bg-[#f8f9fa] border rounded-xl p-4 font-lato">
                                    <div className="text-green-600 mb-2 font-lato">{item.icon}</div>
                                    <h4 className="text-md font-semibold text-gray-800 font-lato">{item.title}</h4>
                                    <p className="text-sm text-gray-500 font-lato">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-4">
                            {["12k+", "4.9/5", "24/7"].map((val, i) => (
                                <div key={i} className="bg-white border rounded-xl px-5 py-3 text-left">
                                    <p className="font-bold text-xl text-gray-900">{val}</p>
                                    <span className="text-xs text-gray-500">
                                        {i === 0 ? "Learners" : i === 1 ? "Rating" : "Support"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-center font-lato">
                        <div className="w-full max-w-md bg-white border rounded-2xl p-8 shadow-sm">

                            <div className="flex items-center gap-2 text-green-700 text-xs font-semibold bg-green-50 px-3 py-1 rounded-full w-fit mb-4">
                                <Smartphone size={14} className="text-green-600" />
                                Welcome back
                            </div>

                            <h2 className="text-xl font-semibold text-gray-900 mb-2 font-bold">
                                Log in with phone and OTP
                            </h2>

                            <p className="text-sm text-gray-500 mb-4 font-semibold">
                                Use your registered mobile number to receive a one-time password
                            </p>
                            <div className="text-center text-xs text-gray-400 mb-4 relative">
                                <span className="bg-white px-2 relative z-10">
                                    Secure OTP-based sign in
                                </span>
                                <div className="absolute top-1/2 left-0 w-full h-px bg-gray-200"></div>
                            </div>

                            <div className="mb-3">
                                <p className="text-xs text-gray-500 mb-1">Phone number</p>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Enter phone number"
                                        value={phone}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                                            setPhone(value);
                                        }}
                                        className="w-full px-5 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-green-600 pointer-events-none">
                                        <Phone size={18} />
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={handleSendOtp}
                                disabled={loading}
                                className={`w-full py-3 rounded-full mb-4 font-medium ${loading ? "bg-gray-200 text-gray-400" : "bg-green-100 text-green-700"
                                    }`}
                            >
                                {loading ? "Sending..." : "Send OTP"}
                            </button>
                            <div className="mb-2 relative">
                                <div className="flex justify-between items-center mb-1">
                                    <p className="text-xs text-gray-500">Enter OTP</p>
                                    <span className="text-xs text-gray-500 cursor-pointer">
                                        Resend code
                                    </span>
                                </div>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Enter OTP" value={otp}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                                            setOtp(value);
                                        }}
                                        className="w-full px-5 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-sm"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-green-600">
                                        <ShieldCheck size={18} />
                                    </span>
                                </div>
                            </div>

                            <div className="flex justify-between items-center text-xs text-gray-400 mb-4">
                                <span>OTP valid for 15 minutes</span>
                                <span className="flex items-center gap-1 text-green-600">
                                    🛡 SSL secured
                                </span>
                            </div>
                            <button
                                onClick={handleVerifyOtp}
                                disabled={loading || !otpSent}
                                className="w-full bg-[#15803d] text-white py-3 rounded-xl font-extrabold hover:bg-green-700"
                            >
                                {loading ? "Verifying..." : "Verify & Log In →"}
                            </button>
                            <div className="grid grid-cols-2 gap-3 mt-5 font-lato">
                                <div className="bg-[#f1f5f9] p-4 rounded-xl text-xs text-gray-600 border border-gray-100 shadow-sm hover:shadow-md transition">
                                    <p className="font-semibold text-gray-800 mb-1">
                                        Continue enrolled courses
                                    </p>
                                    <p className="text-[11px] text-gray-500 leading-relaxed">
                                        Resume modules, assignments, and reading lists instantly.
                                    </p>
                                </div>
                                <div className="bg-[#f1f5f9] p-4 rounded-xl text-xs text-gray-600 border border-gray-100 shadow-sm hover:shadow-md transition">
                                    <p className="font-semibold text-gray-800 mb-1">
                                        View earned certificates
                                    </p>
                                    <p className="text-[11px] text-gray-500 leading-relaxed">
                                        Keep all completions and credentials in one place.
                                    </p>
                                </div>

                            </div>

                            <p className="text-center  text-sm font-bold mt-4 text-gray-500">
                                New to platform?{" "}
                                <span className="text-green-600 font-bold cursor-pointer font-semibold">
                                    Create an account
                                </span>
                            </p>

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;